import { useEffect, useRef } from 'react';
import { channelMetaFrom } from '@visious/core';
import { fetchChannelDetails } from '../../api/channels';
import { getPlatform } from '../../platform/instance';
import { putChannelMeta, touchChannelMeta, useChannelMetaCache } from '../../platform/channelMetaStore';
import { systemClock } from '../../platform/clock';
import { subscriberLine } from './model';

/**
 * The byline's subscriber count, like on the channel page — through the R14 channel-meta
 * cache, because the video payload has no numeric count to give: `subCountText` is a pre-formatted
 * string the parser deliberately does not read (R7). One ~50 KB channel fetch per channel not
 * already cached, which is the same price the manage grid's avatars pay, amortised by the same
 * 30-day cache; a repeat visit to any of the channel's videos costs nothing.
 *
 * The count renders as absence until it arrives — a byline flashing "0 subscribers" would be a
 * claim, the About-tab lesson.
 */
export function useAuthorSubscriberText(channelId: string | null): string | null {
  const cache = useChannelMetaCache();
  const inFlight = useRef(new Set<string>());
  const entry = channelId === null ? undefined : cache.entries.find((meta) => meta.id === channelId);
  const missing = channelId !== null && entry === undefined;

  useEffect(() => {
    if (channelId === null || !missing || inFlight.current.has(channelId)) return;
    inFlight.current.add(channelId);

    void (async () => {
      try {
        const boot = await getPlatform();
        if (!boot.ok) return;
        const result = await fetchChannelDetails(boot.platform, channelId);
        if (!result.ok) return;
        putChannelMeta([
          channelMetaFrom(
            boot.platform.config,
            result.value,
            Math.floor(boot.platform.clock.nowEpochMs() / 1000),
          ),
        ]);
      } finally {
        inFlight.current.delete(channelId);
      }
    })();
  }, [channelId, missing]);

  // A hit keeps its LRU slot alive — the store ignores same-second touches, so this is per-render safe.
  useEffect(() => {
    if (entry !== undefined) {
      touchChannelMeta([entry.id], Math.floor(systemClock.nowEpochMs() / 1000));
    }
  }, [entry]);

  return subscriberLine(entry?.subscriberCount ?? null);
}
